import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  X, ChevronLeft, ChevronRight, Building2, MapPin, BedDouble,
  Bath, Eye, Edit2, Send, Calendar, Pound, Tag, CheckCircle2,
  MessageSquare, ArrowLeft
} from 'lucide-react';
import { marketplaceAPI, messagesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const statusColors = {
  active: 'bg-green-100 text-green-800',
  under_offer: 'bg-amber-100 text-amber-800',
  closed: 'bg-gray-100 text-gray-800',
  pending: 'bg-blue-100 text-blue-800',
};

const statusLabels = {
  active: 'Active',
  under_offer: 'Under Offer',
  closed: 'Closed',
  pending: 'Pending',
};

const ListingDetailModal = ({
  listing,
  mode,             // 'landlord' | 'marketplace'
  onClose,
  onUpdate,         // landlord only: (updatedListing) => void
  onMakeOffer,      // marketplace only: (listing) => void
  hasOffer = false, // marketplace only
}) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    address: listing.address || '',
    city: listing.city || '',
    postcode: listing.postcode || '',
    bedrooms: listing.bedrooms || 1,
    bathrooms: listing.bathrooms || 1,
    monthlyRent: listing.monthlyRent || 0,
    deposit: listing.deposit || 0,
    description: listing.description || '',
    propertyType: listing.propertyType || 'apartment',
    availableFrom: listing.availableFrom?.split('T')[0] || '',
    minTerm: listing.minTerm || 12,
    petFriendly: listing.petFriendly || false,
    furnished: listing.furnished || false,
    utilitiesIncluded: listing.utilitiesIncluded || false,
    status: listing.status || 'active',
  });
  const [saving, setSaving] = useState(false);

  // ── Deal chat state (marketplace mode only) ──
  const [dealView, setDealView] = useState('details'); // 'details' | 'messages'
  const [dealConversation, setDealConversation] = useState(null);
  const [dealMessages, setDealMessages] = useState([]);
  const [dealChatInput, setDealChatInput] = useState('');
  const [dealLoading, setDealLoading] = useState(false);
  const [dealSending, setDealSending] = useState(false);
  const dealPollRef = useRef(null);
  const dealMessagesEndRef = useRef(null);
  const dealConversationRef = useRef(null);

  const { toast } = useToast();
  const { getAuthToken, user } = useAuth();

  const images = listing.images || [];
  const hasImages = images.length > 0;

  // Keep ref in sync for polling callback
  useEffect(() => { dealConversationRef.current = dealConversation; }, [dealConversation]);

  // Auto-scroll deal messages
  useEffect(() => {
    dealMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [dealMessages]);

  // Cleanup poll on unmount or when switching back to details
  useEffect(() => {
    if (dealView === 'details' && dealPollRef.current) {
      clearInterval(dealPollRef.current);
      dealPollRef.current = null;
    }
    return () => {
      if (dealPollRef.current) clearInterval(dealPollRef.current);
    };
  }, [dealView]);

  const startPolling = useCallback((convId) => {
    if (dealPollRef.current) clearInterval(dealPollRef.current);
    dealPollRef.current = setInterval(async () => {
      try {
        const token = getAuthToken();
        const data = await messagesAPI.getConversation(convId, token);
        setDealMessages(data.messages || []);
      } catch {}
    }, 5000);
  }, [getAuthToken]);

  const fetchDealConversation = async () => {
    try {
      const token = getAuthToken();
      const convs = await messagesAPI.getConversations(token, 'guest');
      const match = convs.find(
        c => c.conversationType === 'deal' && c.relatedId === listing._id
      );
      if (match) {
        setDealConversation(match);
        const data = await messagesAPI.getConversation(match._id, token);
        setDealMessages(data.messages || []);
        startPolling(match._id);
      }
    } catch (err) {
      console.error('fetchDealConversation error', err);
    }
  };

  const handleOpenMessages = async () => {
    setDealView('messages');
    setDealLoading(true);
    await fetchDealConversation();
    setDealLoading(false);
  };

  const handleSendDealMessage = async () => {
    if (!dealChatInput.trim() || dealSending) return;
    const msgText = dealChatInput.trim();
    setDealChatInput('');
    setDealSending(true);
    try {
      const token = getAuthToken();
      if (!dealConversationRef.current) {
        // First message — create the conversation with the initial message
        const conv = await messagesAPI.createConversation({
          participantId: listing.landlordId,
          conversationType: 'deal',
          participantType: 'landlord',
          initialMessage: msgText,
          relatedId: listing._id,
        }, token);
        setDealConversation(conv);
        // Fetch messages (the initial message is stored server-side)
        const data = await messagesAPI.getConversation(conv._id, token);
        setDealMessages(data.messages || []);
        startPolling(conv._id);
      } else {
        // Subsequent message
        const msg = await messagesAPI.sendMessage({
          conversationId: dealConversationRef.current._id,
          content: msgText,
        }, token);
        setDealMessages(prev => [...prev, msg]);
      }
    } catch (err) {
      setDealChatInput(msgText); // restore on failure
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' });
    } finally {
      setDealSending(false);
    }
  };

  const prevImage = () =>
    setActiveImageIndex(i => (i === 0 ? images.length - 1 : i - 1));
  const nextImage = () =>
    setActiveImageIndex(i => (i === images.length - 1 ? 0 : i + 1));

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = getAuthToken();
      const updated = await marketplaceAPI.updateListing(listing._id, editData, token);
      toast({ title: 'Saved', description: 'Listing updated successfully' });
      onUpdate(updated);
      setIsEditing(false);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to save changes',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData({
      ...listing,
      availableFrom: listing.availableFrom?.split('T')[0] || '',
    });
    setIsEditing(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Immediately';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric',
      });
    } catch { return dateStr; }
  };

  const formatCurrency = (val) =>
    val != null ? `£${Number(val).toLocaleString('en-GB')}` : '—';

  return (
    <div
      className="fixed inset-0 z-[55] bg-black/50"
      onClick={onClose}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-3xl bg-[#F5F0E8] shadow-2xl overflow-y-auto flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* ── A) Sticky Header ── */}
        <div className="sticky top-0 z-10 bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <button
            onClick={dealView === 'messages' ? () => setDealView('details') : onClose}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors"
          >
            {dealView === 'messages' ? <ArrowLeft size={20} /> : <X size={20} />}
            <span className="text-sm font-medium">
              {dealView === 'messages' ? 'Back to details' : 'Close'}
            </span>
          </button>

          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" size="sm" onClick={handleCancelEdit} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
              </>
            ) : mode === 'landlord' ? (
              <Button
                size="sm"
                variant="outline"
                className="border-[#1F3A68] text-[#1F3A68] hover:bg-[#1F3A68]/10"
                onClick={() => setIsEditing(true)}
              >
                <Edit2 size={14} className="mr-1.5" /> Edit Listing
              </Button>
            ) : dealView === 'details' ? (
              /* Marketplace — details view: show both action buttons */
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#1F3A68] text-[#1F3A68] hover:bg-[#1F3A68]/10"
                  onClick={handleOpenMessages}
                >
                  <MessageSquare size={14} className="mr-1.5" />
                  {dealConversation ? 'View Chat' : 'Message'}
                </Button>
                {hasOffer ? (
                  <span className="px-3 py-1.5 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                    Offer Sent ✓
                  </span>
                ) : (
                  <Button
                    size="sm"
                    className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
                    onClick={() => onMakeOffer(listing)}
                  >
                    <Send size={14} className="mr-1.5" /> Make Offer
                  </Button>
                )}
              </>
            ) : null /* messages view — header handled by back button */}
          </div>
        </div>

        {/* ── B) Image Gallery ── */}
        <div className="relative bg-gradient-to-br from-gray-200 to-gray-300 h-72 md:h-96 flex-shrink-0">
          {hasImages ? (
            <>
              <img
                src={images[activeImageIndex]}
                alt={listing.address}
                className="w-full h-full object-cover"
              />
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveImageIndex(idx)}
                        className={`w-14 h-14 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${
                          idx === activeImageIndex
                            ? 'border-white scale-110 shadow-lg'
                            : 'border-white/40 opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                  <span className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                    {activeImageIndex + 1} / {images.length}
                  </span>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Building2 size={80} className="text-gray-400" />
            </div>
          )}
        </div>

        {/* ── Content area ── */}
        <div className="flex-1 pb-28">

          {/* ── DEAL CHAT VIEW (marketplace messages tab) ── */}
          {dealView === 'messages' && mode === 'marketplace' && (
            <div className="mx-6 mt-4 bg-white rounded-xl shadow-sm overflow-hidden flex flex-col"
              style={{ height: 'calc(100vh - 440px)', minHeight: '320px' }}>
              {/* Chat header */}
              <div className="px-4 py-3 border-b bg-[#1F3A68]/5 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <MessageSquare size={16} className="text-[#1F3A68]" />
                  <span className="font-semibold text-[#1F3A68] text-sm">Message Landlord</span>
                  <span className="text-xs text-gray-400 truncate">— {listing.address}</span>
                </div>
              </div>

              {/* Messages list */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {dealLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A68]" />
                  </div>
                ) : dealMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <MessageSquare size={40} className="text-gray-300 mb-3" />
                    <p className="text-gray-500 text-sm font-medium">No messages yet</p>
                    <p className="text-gray-400 text-xs mt-1">
                      Send a message to the landlord to start the conversation
                    </p>
                  </div>
                ) : (
                  dealMessages.map((msg) => {
                    const isOwn = msg.senderId === user?.id;
                    return (
                      <div key={msg._id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                          isOwn
                            ? 'bg-[#1F3A68] text-white rounded-tr-sm'
                            : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                        }`}>
                          {!isOwn && (
                            <p className="text-xs font-semibold opacity-70 mb-1">{msg.senderName}</p>
                          )}
                          <p className="text-sm leading-relaxed">{msg.content}</p>
                          <p className={`text-[10px] mt-1 ${isOwn ? 'text-white/60' : 'text-gray-400'}`}>
                            {new Date(msg.createdAt).toLocaleTimeString([], {
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={dealMessagesEndRef} />
              </div>

              {/* Input area */}
              <div className="border-t p-3 flex gap-2 bg-white flex-shrink-0">
                <input
                  type="text"
                  value={dealChatInput}
                  onChange={e => setDealChatInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendDealMessage();
                    }
                  }}
                  placeholder="Type a message to the landlord…"
                  disabled={dealSending}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68] disabled:opacity-50"
                />
                <Button
                  size="sm"
                  className="bg-[#1F3A68] hover:bg-[#1F3A68]/90 px-3 flex-shrink-0"
                  onClick={handleSendDealMessage}
                  disabled={dealSending || !dealChatInput.trim()}
                >
                  <Send size={16} />
                </Button>
              </div>
            </div>
          )}

          {/* ── DETAILS VIEW ── */}
          {(dealView === 'details' || mode === 'landlord') && (
            <>
              {isEditing ? (
                /* ── EDIT MODE: single card with full form ── */
                <div className="bg-white mx-6 mt-4 rounded-xl shadow-sm px-6 py-5 space-y-4">
                  <h2 className="text-lg font-bold text-[#1F3A68] mb-2">Edit Listing</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <Input
                        value={editData.address}
                        onChange={e => setEditData(p => ({ ...p, address: e.target.value }))}
                        placeholder="Street address"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <Input
                        value={editData.city}
                        onChange={e => setEditData(p => ({ ...p, city: e.target.value }))}
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                      <Input
                        value={editData.postcode}
                        onChange={e => setEditData(p => ({ ...p, postcode: e.target.value }))}
                        placeholder="Postcode"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                      <select
                        value={editData.propertyType}
                        onChange={e => setEditData(p => ({ ...p, propertyType: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]"
                      >
                        <option value="apartment">Apartment</option>
                        <option value="house">House</option>
                        <option value="studio">Studio</option>
                        <option value="room">Room</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={editData.status}
                        onChange={e => setEditData(p => ({ ...p, status: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]"
                      >
                        <option value="active">Active</option>
                        <option value="under_offer">Under Offer</option>
                        <option value="closed">Closed</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
                      <Input
                        type="number" min="1"
                        value={editData.bedrooms}
                        onChange={e => setEditData(p => ({ ...p, bedrooms: parseInt(e.target.value) || 1 }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
                      <Input
                        type="number" min="1"
                        value={editData.bathrooms}
                        onChange={e => setEditData(p => ({ ...p, bathrooms: parseInt(e.target.value) || 1 }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Rent (£)</label>
                      <Input
                        type="number" min="0"
                        value={editData.monthlyRent}
                        onChange={e => setEditData(p => ({ ...p, monthlyRent: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Deposit (£)</label>
                      <Input
                        type="number" min="0"
                        value={editData.deposit}
                        onChange={e => setEditData(p => ({ ...p, deposit: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Available From</label>
                      <Input
                        type="date"
                        value={editData.availableFrom}
                        onChange={e => setEditData(p => ({ ...p, availableFrom: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Min Term (months)</label>
                      <Input
                        type="number" min="1"
                        value={editData.minTerm}
                        onChange={e => setEditData(p => ({ ...p, minTerm: parseInt(e.target.value) || 1 }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <Textarea
                        rows={4}
                        value={editData.description}
                        onChange={e => setEditData(p => ({ ...p, description: e.target.value }))}
                        placeholder="Describe the property…"
                      />
                    </div>
                  </div>

                  {/* Checkboxes */}
                  <div className="flex flex-wrap gap-6 pt-2">
                    {[
                      { key: 'furnished', label: 'Furnished' },
                      { key: 'utilitiesIncluded', label: 'Bills Included' },
                      { key: 'petFriendly', label: 'Pet Friendly' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={editData[key]}
                          onChange={e => setEditData(p => ({ ...p, [key]: e.target.checked }))}
                          className="w-4 h-4 accent-[#1F3A68]"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

              ) : (
                /* ── VIEW MODE ── */
                <>
                  {/* C) Address / Status / Price */}
                  <div className="bg-white mx-6 mt-4 rounded-xl shadow-sm px-6 py-5">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h1 className="text-2xl font-bold text-[#1F3A68] leading-tight">{listing.address}</h1>
                      <span className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold ${statusColors[listing.status] || 'bg-gray-100 text-gray-700'}`}>
                        {statusLabels[listing.status] || listing.status}
                      </span>
                    </div>
                    <p className="flex items-center gap-1.5 text-gray-600 text-sm mb-4">
                      <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                      {listing.city}{listing.postcode ? `, ${listing.postcode}` : ''}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-[#1F3A68]">
                        {formatCurrency(listing.monthlyRent)}
                      </span>
                      <span className="text-lg text-gray-500">/month</span>
                    </div>
                  </div>

                  {/* D) Key stats */}
                  <div className="bg-white mx-6 mt-3 rounded-xl shadow-sm px-6 py-4">
                    <div className="flex flex-wrap gap-6">
                      <div className="flex items-center gap-2 text-gray-700">
                        <BedDouble size={18} className="text-[#1F3A68]" />
                        <span className="font-medium">{listing.bedrooms}</span>
                        <span className="text-gray-500">{listing.bedrooms === 1 ? 'Bedroom' : 'Bedrooms'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Bath size={18} className="text-[#1F3A68]" />
                        <span className="font-medium">{listing.bathrooms}</span>
                        <span className="text-gray-500">{listing.bathrooms === 1 ? 'Bathroom' : 'Bathrooms'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Building2 size={18} className="text-[#1F3A68]" />
                        <span className="text-gray-500 capitalize">{listing.propertyType || 'Property'}</span>
                      </div>
                    </div>
                  </div>

                  {/* E) Feature badges */}
                  {(listing.furnished || listing.utilitiesIncluded || listing.petFriendly) && (
                    <div className="bg-white mx-6 mt-3 rounded-xl shadow-sm px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {listing.furnished && (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1F3A68]/10 text-[#1F3A68] rounded-full text-sm font-medium">
                            <CheckCircle2 size={13} /> Furnished
                          </span>
                        )}
                        {listing.utilitiesIncluded && (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                            <CheckCircle2 size={13} /> Bills Included
                          </span>
                        )}
                        {listing.petFriendly && (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                            <CheckCircle2 size={13} /> Pet Friendly
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* F) Description */}
                  {listing.description && (
                    <div className="bg-white mx-6 mt-3 rounded-xl shadow-sm px-6 py-4">
                      <h3 className="text-base font-semibold text-[#1F3A68] mb-2">About this property</h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm">
                        {listing.description}
                      </p>
                    </div>
                  )}

                  {/* G) Property details grid */}
                  <div className="bg-white mx-6 mt-3 rounded-xl shadow-sm px-6 py-4">
                    <h3 className="text-base font-semibold text-[#1F3A68] mb-4">Property Details</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {[
                        { label: 'Deposit', value: formatCurrency(listing.deposit) },
                        { label: 'Available From', value: formatDate(listing.availableFrom) },
                        { label: 'Min Term', value: listing.minTerm ? `${listing.minTerm} months` : '—' },
                        { label: 'Postcode', value: listing.postcode || '—' },
                        { label: 'Views', value: `${listing.views || 0} views` },
                        {
                          label: 'Listed',
                          value: listing.createdAt
                            ? new Date(listing.createdAt).toLocaleDateString('en-GB')
                            : '—',
                        },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">{label}</div>
                          <div className="font-semibold text-gray-800 text-sm">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* ── H) Bottom sticky action bar ── */}
        {!isEditing && dealView === 'details' && (
          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex gap-3 flex-shrink-0">
            {mode === 'landlord' ? (
              <Button
                className="flex-1 bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                onClick={() => setIsEditing(true)}
              >
                <Edit2 size={16} className="mr-2" /> Edit Listing
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="flex-1 border-[#1F3A68] text-[#1F3A68] hover:bg-[#1F3A68]/10"
                  onClick={handleOpenMessages}
                >
                  <MessageSquare size={16} className="mr-2" />
                  {dealConversation ? 'View Chat' : 'Message Landlord'}
                </Button>
                {hasOffer ? (
                  <div className="flex-1 text-center py-2.5 px-4 bg-blue-100 text-blue-800 rounded-lg font-medium text-sm">
                    Offer Already Sent
                  </div>
                ) : (
                  <Button
                    className="flex-1 bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
                    onClick={() => onMakeOffer(listing)}
                  >
                    <Send size={16} className="mr-2" /> Make Offer
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListingDetailModal;
